// Import angular
import { Component, OnInit, ViewChildren, HostListener, QueryList, AfterViewInit, OnDestroy } from '@angular/core';

// Import rxjs
import { Subject, takeUntil } from 'rxjs';

// Import types
import { CalculationLineApi, CalculationMetaApi, CalculationTailApi } from '../types/calculation';

// Import services
import { UndoService } from '../services/undo.service';
import { DataService } from '../services/data.service';
import { ApplicationContextService } from '../services/application-context.service';
import { ApplicationEventService } from '../services/application-event.service';
import { MessageService, MessageType } from '../services/message.service';
import { LoaderService } from '../loader/loader.service';
import { ImportService, IMPORTABLE_CUF_FILE_EXTENSIONS, InvalidXmlError, InvalidCufError } from '../services/import.service';

// Import utils
import { FileUtils } from '../utils';

// Import components
import { PagedLineTableComponent } from '../tables/paged-line-table/paged-line-table.component';

import { Store } from '@ngrx/store';
import { AppState } from '../state-management/app-state';
import * as CalculationActions from '../state-management/calculation-actions';
import { selectCalculationMeta, selectCurrentSelection, selectLines, selectTail } from '../state-management/calculation-selectors';
import { CalculationSelection, isCalculationLineApi, isCalculationTailApi } from '../state-management/calculation-state';
import { undoRedo } from '../app.module';
import { CreateLineService } from '../services/create-line/create-line.service';

@Component({
    selector: 'tab-view-container',
    templateUrl: './tab-view-container.component.html',
    styleUrls: ['./tab-view-container.component.scss'],
    standalone: false
})
export class TabViewContainerComponent implements AfterViewInit, OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();

    private _selection: CalculationSelection;
    private _meta: CalculationMetaApi;
    private _lines: CalculationLineApi[] = [];
    private _tail: CalculationTailApi[] = [];

    @ViewChildren('table') public _tableComponents: QueryList<PagedLineTableComponent>;

    public _selectedTabIndex: number = 0;
    public _draggingFile: boolean = false;

    // private subcriptions: Subscription[] = [];

    constructor(
        private dataService: DataService,
        private applicationContextService: ApplicationContextService,
        private applicationEventService: ApplicationEventService,
        private messageService: MessageService,
        private loaderService: LoaderService,
        private importService: ImportService,
        private store: Store<AppState>,
        private createLineService: CreateLineService
    ) {
        this.store.select(selectCurrentSelection).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(selection => this._selection = selection);

        this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(meta => this._meta = meta);

        this.store.select(selectLines).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(lines => this._lines = lines);

        this.store.select(selectTail).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(tail => this._tail = tail);


    }

    public ngOnInit(): void {
    }

    public ngOnDestroy(): void {
        // Unsubscibe
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public ngAfterViewInit(): void {
        // Init app context values
        this._handleTabChange(0);
    }

    public async deleteSelection() {
        // Get selection
        if (!this._selection || !this._selection.selectedLines?.length) {
            return;
        }

        const selectedCalculation = this._selection.selectedLines?.filter(f => isCalculationLineApi(f))
        const selectedTail = this._selection.selectedLines?.filter(f => isCalculationTailApi(f))


        if (selectedCalculation.length > 0) {
            selectedCalculation.forEach((line: CalculationLineApi) => this.store.dispatch(CalculationActions.deleteLine({
                projectId: this._meta.projectId,
                calculationId: this._meta.id,
                line
            })))
        } else if (selectedTail.length > 0) {
            selectedTail.forEach((line: CalculationTailApi) => this.store.dispatch(CalculationActions.deleteTailLine({
                projectId: this._meta.projectId,
                calculationId: this._meta.id,
                line
            })))
        }
    }

    /* --- Event handlers --- */

    public _handleTabChange(tab: number) {
        // Set the current selected tab index
        this._selectedTabIndex = tab;

        // Update tab change in application context
        this.applicationContextService.set('activeTabIndex', tab);

        // Get the active table
        const activeTable = this._tableComponents.find((c, i) => i === this._selectedTabIndex);

        // Update the active table in application context
        this.applicationContextService.set('activeTable', activeTable);
    }

    public _handleKeydown(event: KeyboardEvent) {
        const activeTable = this._tableComponents.find(() => true);
        // if (!activeTable) return;

        // const isSideNavigatorOpen = this.applicationContextService.get<boolean>('sideNavigatorOpen').value;
        // if (isSideNavigatorOpen) return;

        // Handle selection changes
        if (event.key === 'Tab' || event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            // Prevent default behavior
            event.preventDefault();
            event.stopPropagation();

            // Move selection
            if (event.key === 'ArrowRight' || (event.key === 'Tab' && !event.shiftKey))
                activeTable.moveSelectionRight(event.shiftKey && event.key !== 'Tab');
            if (event.key === 'ArrowLeft' || (event.key === 'Tab' && event.shiftKey))
                activeTable.moveSelectionLeft(event.shiftKey && event.key !== 'Tab');
            if (event.key === 'ArrowUp')
                activeTable.moveSelectionUp(event.shiftKey);
            if (event.key === 'ArrowDown')
                activeTable.moveSelectionDown(event.shiftKey);

            // Make sure focus is not handled
            return;
        }

        // Handle focus
        if (event.key === 'Enter') {
            // Prevent default behavior
            event.preventDefault();
            event.stopPropagation();

            activeTable.focusSelectedCell(false);
        } else if (event.key !== 'Shift' && !event.ctrlKey && event.key !== 'F10' && event.key !== 'F8' && event.key !== 'F9' && event.key !== 'F7' && event.key !== 'F6' && event.key !== 'Meta' && !event.metaKey) {
            activeTable.focusSelectedCell();
        }
    }

    @HostListener('window:keydown.meta.f', ['$event'])
    @HostListener('window:keydown.control.f', ['$event'])
    public _handleSearch(event: KeyboardEvent) {
        event.preventDefault();

        this.applicationEventService.emit('search');
    }

    @HostListener('window:keydown.meta.z', ['$event'])
    @HostListener('window:keydown.control.z', ['$event'])
    public _handleUndo(event: KeyboardEvent) {
        event.preventDefault();

        undoRedo.handleCalculationUndo(this.store);
    }

    @HostListener('window:keydown.meta.y', ['$event'])
    @HostListener('window:keydown.control.y', ['$event'])
    public _handleRedo(event: KeyboardEvent) {
        event.preventDefault();

        undoRedo.handleCalculationRedo(this.store);
    }

    @HostListener('window:keydown.f10', ['$event'])
    public _handleDeleteShortcut(event: KeyboardEvent) {
        // Stop further propagation
        event.stopPropagation();
        event.preventDefault();

        // Delete selection
        this.deleteSelection();
    }

    @HostListener('window:keydown.f9', ['$event'])
     public async _handleInsertRow(event: KeyboardEvent, isText: boolean = false) {
        // Get selection
        if (!this._selection || !this._selection.selectedLines?.length) {
            return;
        }

        const selectedLine = this._selection.selectedLines[0];

        if (isCalculationLineApi(selectedLine)) {
            await this.createLineService.createCalculationLine(isText, !event.shiftKey);
            // const line = createLine(this._lines, selectedLine.line, isText, !event.shiftKey);
            // this.store.dispatch(CalculationActions.insertLine({
            //     projectId: this._meta.projectId,
            //     calculationId: this._meta.id,
            //     line
            // }))
        } else if (isCalculationTailApi(selectedLine)) {
            await this.createLineService.createTailLine(isText);
            // const line = createTailLine(this._tail, selectedLine.line, isText);
            // this.store.dispatch(CalculationActions.insertTailLine({
            //     projectId: this._meta.projectId,
            //     calculationId: this._meta.id,
            //     line
            // }))
        }


        // if (selectedLine) {
        //     // Get the currently selected line
        //     const selectedLine = this.applicationContextService.selectedLineValue as Line;
        //     if (!selectedLine) return;

        //     // Get the code
        //     const code = selectedLine.code;
        //     const catCode = code.split('.')[0];

        //     // Check template if subcategory is text line only
        //     const templ = this.applicationDataService.activeCalculation.template;
        //     const subCatTempl = templ.find(c => c.categoryCode === catCode)?.subcategories.find(s => s.subcategoryCode === code);

        //     // Insert a line
        //     this.insertLine({
        //         belowSelection: !event.shiftKey,
        //         type: isText || subCatTempl?.textLinesOnly ? 'text' : 'calculation'
        //     });
        // } else if (activeTable instanceof TailTableComponent) {
        //     // Insert a line
        //     this.insertLine({
        //         belowSelection: !event.shiftKey,
        //         type: isText ? 'text' : 'calculation'
        //     });
        // }
    }

    @HostListener('window:keydown.shift.f9', ['$event'])
    public _handleInsertRowShift(event: KeyboardEvent) {
        this._handleInsertRow(event);
    }

    @HostListener('window:keydown.f8', ['$event'])
    public _handleInsertTextRow(event: KeyboardEvent) {
        this._handleInsertRow(event, true);
    }

    @HostListener('window:keydown.shift.f8', ['$event'])
    public _handleInsertTextRowShift(event: KeyboardEvent) {
        this._handleInsertRow(event, true);
    }

    /**
     * This handler will process alt+number combinations
     * @param event 
     * @returns 
     */
    @HostListener('window:keydown', ['$event'])
    public _listenToAltCombi(event?: KeyboardEvent) {
        if (!event.altKey) {
            // Send event to the handler that will process navigation shortcuts
            this._handleKeydown(event);
            return;
        }

        switch (event.code) {
            case 'Digit1':
                // Insert row
                this._handleInsertRow(event);
                break;
            case 'Digit2':
                // Insert text row
                this._handleInsertRow(event, true);
                break;
            case 'Digit3':
                // Delete line
                this._handleDeleteShortcut(event);
                break;
            case 'Digit5':
                // Toggle line type
                this.applicationEventService.emit('convertSelectedLineType');
                break;
            default: return;
        }
    }

    public _handleDrop(event: DragEvent) {
        // Prevent default event handling
        event.preventDefault();

        // Stop dragging file
        this._draggingFile = false;

        // Process items
        const [file] = Array.from(event.dataTransfer.items).map(i => i.getAsFile());

        // Get the file extension
        const extension = file.name.replace(/.*(?=\.\w+$)/, '');

        // Ensure extension is valid
        if (!IMPORTABLE_CUF_FILE_EXTENSIONS.includes(extension.toLowerCase())) {
            return this.messageService.show({
                type: MessageType.Error,
                title: 'Fout',
                message: `Kan bestand van type '${extension}' niet importeren. De volgende types zijn toegestaan: ${IMPORTABLE_CUF_FILE_EXTENSIONS.join(' ')}.`
            });
        }

        this.loaderService.performAsyncTask(async () => {
            try {
                // Load the file
                const contents = await FileUtils.loadFile(file);

                // Import the file
                await this.importService.importCuf(contents);
            } catch (err) {
                if (err instanceof InvalidXmlError || err instanceof InvalidCufError) {
                    // Show message
                    this.messageService.show({
                        type: MessageType.Error,
                        title: 'Fout',
                        message: 'Het bestand is niet in CUF formaat.'
                    });
                } else {
                    // Report the error
                    this.dataService.reportError(err);

                    // Show message
                    this.messageService.show({
                        type: MessageType.Error,
                        title: 'Fout',
                        message: 'Er is een onverwachte fout opgetreden tijds en importeren. Probeer het later nog een keer.'
                    });
                }
            }
        });
    }

    public _handleDragEnter(event: DragEvent) {
        if (event.dataTransfer.items.length > 1) return;
        if (event.dataTransfer.items[0].type !== 'text/xml' && event.dataTransfer.items[0].type !== '') return;

        this._draggingFile = true;
    }

    public _handleDragLeave(event: DragEvent) {
        this._draggingFile = false;
    }
}
